/**
 * PrimaryKeyPopovers — Designer-side popover wrappers for the add + drop PK
 * forms. Sqlite renders disabled triggers with the recreate-path tooltip; the
 * form bodies are never mounted on sqlite. Mirrors `ColumnAddPopover` /
 * `ColumnFormPopover` patterns from Slice A1.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B1.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import {
  getAddPrimaryKeyUnsupportedTooltip,
  isAddPrimaryKeySupported,
} from '../../edit/edits/addPrimaryKey';
import {
  getDropPrimaryKeyUnsupportedTooltip,
  isDropPrimaryKeySupported,
} from '../../edit/edits/dropPrimaryKey';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { PrimaryKeyAddFormBody } from './PrimaryKeyAddFormBody';
import { PrimaryKeyDropFormBody } from './PrimaryKeyDropFormBody';

export interface PrimaryKeyAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function PrimaryKeyAddPopover(props: PrimaryKeyAddPopoverProps) {
  const { editContext, testId = 'primary-key-add-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddPrimaryKeySupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAddPrimaryKeyUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Primary key
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add primary key"
        >
          <Plus className="h-3.5 w-3.5" />
          Primary key
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <PrimaryKeyAddFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export interface PrimaryKeyDropPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function PrimaryKeyDropPopover(props: PrimaryKeyDropPopoverProps) {
  const { editContext, testId = 'primary-key-drop-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isDropPrimaryKeySupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getDropPrimaryKeyUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Drop primary key
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Drop primary key"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Drop primary key
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="w-80"
        data-testid={testId}
      >
        {open ? (
          <PrimaryKeyDropFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
